import React from 'react';
import { Checkbox } from '@roger/ui-components';
import type {
  EpisodeUnsentBillingCycleResponse,
  EpisodicClaimStatus,
} from '../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../utils/dateUtils';

const STATUS_LABELS: Partial<Record<EpisodicClaimStatus, string>> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Pending Approval',
  READY_TO_SEND: 'Ready to Send',
};

const cycleStatusLabel = (status: EpisodicClaimStatus): string =>
  STATUS_LABELS[status] ?? status;

interface EpisodeInsuranceVoidCyclesListProps {
  cycles: EpisodeUnsentBillingCycleResponse[];
  checkedIds: ReadonlySet<string>;
  onToggle: (cycleId: string, checked: boolean) => void;
  oldInsuranceName: string;
  newInsuranceName: string;
  disabled?: boolean;
}

/**
 * The per-cycle choice an error correction offers (EMR-2361), rendered inline
 * in the insurance editor.
 *
 * The episode's unsent cycles under the old insurance may still need to be
 * billed to it, so nothing is voided without the user choosing. The parent
 * owns the checked set (it starts with every cycle checked) so one Save can
 * read it.
 */
export const EpisodeInsuranceVoidCyclesList: React.FC<
  EpisodeInsuranceVoidCyclesListProps
> = ({
  cycles,
  checkedIds,
  onToggle,
  oldInsuranceName,
  newInsuranceName,
  disabled = false,
}) => {
  if (cycles.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        No unsent claims under {oldInsuranceName} to void.
      </p>
    );
  }

  return (
    <div className='space-y-2'>
      <p className='text-sm text-muted-foreground'>
        These claims still bill {oldInsuranceName}. Check the ones to void; each
        period is rebuilt under {newInsuranceName} once it is due.
      </p>
      <div
        className='divide-y rounded-lg border border-border max-h-[40vh] overflow-y-auto'
        data-testid='void-cycle-list'
      >
        {cycles.map(cycle => {
          const checkboxId = `void-cycle-${cycle.id}`;
          return (
            <div key={cycle.id} className='flex items-start gap-3 px-3 py-2'>
              <Checkbox
                id={checkboxId}
                checkboxSize='default'
                checked={checkedIds.has(cycle.id)}
                disabled={disabled}
                onCheckedChange={checked =>
                  onToggle(cycle.id, checked === true)
                }
              />
              <label
                htmlFor={checkboxId}
                className='flex min-w-0 flex-1 flex-col gap-0.5 text-sm md:flex-row md:items-center md:justify-between'
              >
                <span className='font-medium'>
                  {formatDateToMMDDYYYY(cycle.start_date)} –{' '}
                  {formatDateToMMDDYYYY(cycle.end_date)}
                </span>
                <span className='text-xs text-muted-foreground whitespace-nowrap'>
                  {cycleStatusLabel(cycle.status)}
                </span>
              </label>
            </div>
          );
        })}
      </div>
      <p className='text-xs text-muted-foreground'>
        Periods already due are rebuilt now; the rest by the nightly run or a
        Refresh. Notes are copied only to a claim rebuilt now. Worksheets are
        rebuilt for {newInsuranceName}.
      </p>
    </div>
  );
};

export default EpisodeInsuranceVoidCyclesList;
